"use client";

import React from "react";
import { ArrowRightIcon } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import LoadingSpinner from "@/components/ui/loading-spinner";
import { PresetOption } from "@/types/createAgent";
import {
  ActionOptionPreviewGrid,
  hasActionOptionVisual,
} from "../visuals/ActionOptionVisuals";
import "./VibePresetPicker.css";

/**
 * Vibe bundles render as full-width rows of selectable directions — each
 * card carries its own attribute preview so options compare at a glance,
 * with no separate preview panel or wrapper box around the group.
 */
export function VibePresetPicker({
  presets,
  disabled,
  processingPresetId,
  onPresetChatMore,
}: {
  presets: PresetOption[];
  disabled: boolean;
  processingPresetId: string | null;
  onPresetChatMore?: (preset: PresetOption | null) => void;
}) {
  const visiblePresets = presets.slice(0, 3);

  return (
    <div
      data-vibe-preset-picker
      className="vibe-preset-picker grid min-w-0 gap-2.5"
    >
      <div data-vibe-selector-list className="grid min-w-0 grid-cols-1 gap-2">
        {visiblePresets.map(preset => {
          const presetId = `${preset.actionId}-${preset.label}`;
          const isProcessing = processingPresetId === presetId;
          const isDisabled = disabled || isProcessing;
          const hasPreview = hasActionOptionVisual(preset);

          return (
            <button
              key={presetId}
              type="button"
              data-vibe-selector-option
              aria-label={`Explore ${preset.label}`}
              disabled={isDisabled}
              onClick={() => !isDisabled && onPresetChatMore?.(preset)}
              className={cn(
                "min-h-11 min-w-0 touch-manipulation rounded-xl border p-2.5 text-left transition-all duration-200",
                hasPreview
                  ? "grid min-h-[72px] grid-cols-[minmax(0,1fr)_7.25rem] items-center gap-2"
                  : "flex flex-col justify-center gap-1",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                "border-border bg-card hover:border-brand-logo/40 hover:bg-muted/35",
                isDisabled &&
                  "cursor-default opacity-50 hover:border-border hover:bg-card"
              )}
            >
              <span className="flex min-w-0 items-start justify-between gap-2">
                <span className="min-w-0 flex-1 text-sm font-semibold leading-snug text-foreground break-words">
                  {preset.label}
                </span>
                <span className="flex size-5 shrink-0 items-center justify-center text-muted-foreground">
                  {isProcessing ? (
                    <LoadingSpinner
                      size="xs"
                      color="gray"
                      className="shrink-0"
                    />
                  ) : (
                    <ArrowRightIcon
                      size={14}
                      weight="bold"
                      aria-hidden="true"
                    />
                  )}
                </span>
              </span>
              <ActionOptionPreviewGrid
                preset={preset}
                className="vibe-preset-preview min-w-0"
                compactOnMobile
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
